import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';

import { useSettings } from '@hooks/useSettings';

export const customerViewEvaluationMethodsSchema = (isForwarder: boolean) => {
  return z.object({
    evaluationMethods: z
      .object({
        evalMethodCalculator: z.object({
          activateMethod: z.boolean().optional().nullable(),
          globalError: z.string().optional().nullable(),
          marketplaceFees: z.number().optional().nullable(),
          fullfillmentCost: z.number().optional().nullable(),
          storageCost: z.number().optional().nullable(),
          otherCost: z.number().optional().nullable(),
          priceDeviation: z.number().optional().nullable(),
        }),
        evalMethodWebValue: z.object({
          activateMethod: z.boolean().optional().nullable(),
          globalError: z.string().optional().nullable(),
          minimumRate: z.number().optional().nullable(),
        }),
        evalMethodIMADBStatisticalValue: z.object({
          activateMethod: z.boolean().optional().nullable(),
          minimumRate: z.number().optional().nullable(),
        }),
        ...(isForwarder && {
          evalMethodInspection: z.object({
            periodOfInspection: z.number().optional().nullable(),
            inspectionRateOfVAT: z.number().optional().nullable(),
            inspectionRateOfEORI: z.number().optional().nullable(),
            activateMethod: z.boolean().optional().nullable(),
          }),
        }),
        evalMethodAIHSCode: z
          .object({
            range: z
              .object({
                from: z.number().min(0, 'From value should be greater then 0').max(1, 'From value should be less then 1'),
                to: z.number().min(0, 'To value should be greater then 0').max(1, 'To value should be less then 1'),
              })
              .superRefine(({ from, to }, ctx) => {
                if (from > to) {
                  ctx.addIssue({
                    path: ['from'],
                    message: 'From value must be less than to value',
                    code: 'custom',
                  });

                  ctx.addIssue({
                    path: ['to'],
                    message: 'To value must be greater than from value',
                    code: 'custom',
                  });
                }
              }),
            activateMethod: z.boolean().optional().nullable(),
          })
          .optional(),
      })
      .superRefine(({ evalMethodCalculator, evalMethodWebValue, evalMethodIMADBStatisticalValue }, ctx) => {
        if (evalMethodCalculator.activateMethod && evalMethodWebValue.activateMethod) {
          ctx.addIssue({
            path: ['evalMethodCalculator', 'globalError'],
            message: 'You cannot select both `EvalMethod Calculator` and `EvalMethod Web Value`',
            code: 'custom',
          });

          ctx.addIssue({
            path: ['evalMethodWebValue', 'globalError'],
            message: 'You cannot select both `EvalMethod Calculator` and `EvalMethod Web Value`',
            code: 'custom',
          });
        }

        if (Number(evalMethodWebValue?.minimumRate) > 1) {
          ctx.addIssue({
            path: ['evalMethodWebValue', 'minimumRate'],
            message: 'Evaluation method web value minimum rate should be less then 100%',
            code: 'custom',
          });
        }

        if (Number(evalMethodIMADBStatisticalValue?.minimumRate) > 1) {
          ctx.addIssue({
            path: ['evalMethodIMADBStatisticalValue', 'minimumRate'],
            message: 'Evaluation method IMADB statistical value minimum rate should be less then 100%',
            code: 'custom',
          });
        }
      }),
  });
};

export type CustomerViewEvaluationMethodsFormValues = z.infer<ReturnType<typeof customerViewEvaluationMethodsSchema>>;

export const useCustomerViewEvaluationMethodsFormInitializer = ({ customerDetails }) => {
  const { data: settings } = useSettings();

  const customerViewEvaluationMethodsSchemaInstance = customerViewEvaluationMethodsSchema(settings.isForwarder);
  type CustomerViewEvaluationMethodsFormValues = z.infer<typeof customerViewEvaluationMethodsSchemaInstance>;

  const buildFormValues = (cd) => {
    const { evaluationMethods: customerDetailsEvaluationMethods } = cd || {};

    const formValues: any = {
      evaluationMethods: {
        evalMethodAIHSCode: {
          range: {
            from: customerDetailsEvaluationMethods?.evalMethodAIHSCode?.range?.from || 0,
            to: customerDetailsEvaluationMethods?.evalMethodAIHSCode?.range?.to || 0,
          },
          activateMethod: customerDetailsEvaluationMethods?.evalMethodAIHSCode?.activateMethod || false,
        },
        evalMethodCalculator: {
          marketplaceFees: customerDetailsEvaluationMethods?.evalMethodCalculator?.marketplaceFees || 0,
          fullfillmentCost: customerDetailsEvaluationMethods?.evalMethodCalculator?.fullfillmentCost || 0,
          storageCost: customerDetailsEvaluationMethods?.evalMethodCalculator?.storageCost || 0,
          otherCost: customerDetailsEvaluationMethods?.evalMethodCalculator?.otherCost || 0,
          activateMethod: customerDetailsEvaluationMethods?.evalMethodCalculator?.activateMethod || false,
          priceDeviation: customerDetailsEvaluationMethods?.evalMethodCalculator?.priceDeviation || 0,
        },
        evalMethodWebValue: {
          minimumRate: customerDetailsEvaluationMethods?.evalMethodWebValue?.minimumRate || 0,
          activateMethod: customerDetailsEvaluationMethods?.evalMethodWebValue?.activateMethod || false,
        },
        evalMethodIMADBStatisticalValue: {
          minimumRate: customerDetailsEvaluationMethods?.evalMethodIMADBStatisticalValue?.minimumRate || 0,
          activateMethod: customerDetailsEvaluationMethods?.evalMethodIMADBStatisticalValue?.activateMethod || false,
        },
      },
    };

    if (settings.isForwarder) {
      formValues.evaluationMethods.evalMethodInspection = {
        periodOfInspection: customerDetailsEvaluationMethods?.evalMethodInspection?.periodOfInspection || 0,
        inspectionRateOfVAT: customerDetailsEvaluationMethods?.evalMethodInspection?.inspectionRateOfVAT || 0,
        inspectionRateOfEORI: customerDetailsEvaluationMethods?.evalMethodInspection?.inspectionRateOfEORI || 0,
        activateMethod: customerDetailsEvaluationMethods?.evalMethodInspection?.activateMethod || false,
      };
    }

    return formValues;
  };

  const form = useForm<CustomerViewEvaluationMethodsFormValues>({
    initialValues: buildFormValues(customerDetails),
    validate: zodResolver(customerViewEvaluationMethodsSchemaInstance),
    validateInputOnBlur: true,
    onValuesChange: () => {
      form.validate();
    },
  });

  return { form, buildFormValues };
};
